/*
 * Copyright (c) 2021, salesforce.com, inc.
 * SPDX-License-Identifier: BSD-3-Clause
 */

import React, {useState, useEffect} from 'react'
import PropTypes from 'prop-types'
import {FormattedMessage} from 'react-intl'
import {
    Box,
    Button,
    Stack,
    Heading,
    Center,
    Text,
    useMultiStyleConfig
} from '@salesforce/retail-react-app/app/components/shared/ui'

import Link from '../../../components/link'
import EmptySearchResults from '../../product-list/partials/empty-results'
import withRegistration from '../../../components/with-registration'

import {useWishList} from '@salesforce/retail-react-app/app/hooks/use-wish-list'

import {useGlobaleContext} from '../../../contexts/globale'
import WishlistInCart from './wishlist-in-cart'
import useTagManager from '../../../hooks/use-tag-manager'
import {CART_TITLE} from '../../../constants'
import {Helmet} from 'react-helmet'
const ButtonWithRegistration = withRegistration(Button)

const MAX_WISHLIST_ITEMS = 12

const EmptyCart = ({isRegistered}) => {
    const cartStyles = useMultiStyleConfig('CartStyles')
    const {countryISO} = useGlobaleContext()
    const tagManager = useTagManager()
    const {data: wishlist} = useWishList()
    const [mounted, setMounted] = useState(false)

    // Ensure Swiper only renders on client to avoid SSR issues
    useEffect(() => {
        setMounted(true)
    }, [])

    const wishlistItems = wishlist?.customerProductListItems?.slice(0, MAX_WISHLIST_ITEMS) ?? []

    return (
        <Box data-testid="sf-cart-empty" flex="1" minWidth="100%" width="full" background="white">
            <Helmet>
                <title>{CART_TITLE}</title>
            </Helmet>
            <Center>
                <Stack margin={{base: '30px 0', lg: '60px 0'}}>
                    <Stack gap="30px" maxWidth={{base: '280px', lg: 'fit-content'}}>
                        <Heading
                            lineHeight={1}
                            as="h1"
                            align="center"
                            fontSize="34px"
                            fontWeight="400"
                            color="black"
                        >
                            <FormattedMessage
                                defaultMessage="Your Bag is Empty"
                                id="empty_cart.description.empty_bag"
                            />
                        </Heading>

                        {!isRegistered && (
                            <Text align="center" fontSize="16px" fontWeight="300" color="black.100">
                                <FormattedMessage
                                    defaultMessage="Sign in to retrieve your saved items or continue shopping."
                                    id="empty_cart.message.sign_in_or_continue_shopping"
                                />
                            </Text>
                        )}

                        <Stack
                            justify="center"
                            flexDirection={{base: 'column', lg: 'row'}}
                            spacing="10px"
                            alignItems="center"
                        >
                            <Button
                                as={Link}
                                href="/"
                                backgroundColor="black.300"
                                _hover={{backgroundColor: 'black.50'}}
                                color="white"
                                {...cartStyles.emptyCart.actionButtons}
                                onClick={() => {
                                    tagManager.ctaClick(
                                        'cta_click',
                                        'Cart Interaction',
                                        'cta click - Continue Shopping',
                                        'Empty Cart - Continue Shopping',
                                        'Continue Shopping'
                                    )
                                }}
                            >
                                <FormattedMessage
                                    defaultMessage="Continue Shopping"
                                    id="empty_cart.link.continue_shopping"
                                />
                            </Button>

                            {!isRegistered && (
                                <ButtonWithRegistration
                                    ispdp={false}
                                    {...cartStyles.emptyCart.actionButtons}
                                    {...cartStyles.emptyCart.signInButton}
                                    isEmptyCart={true}
                                >
                                    <FormattedMessage
                                        defaultMessage="Sign In"
                                        id="empty_cart.link.sign_in"
                                    />
                                </ButtonWithRegistration>
                            )}
                        </Stack>
                    </Stack>
                </Stack>
            </Center>

            {/* Wishlist Section */}
            {isRegistered && wishlistItems.length > 0 && mounted && (
                <Box padding={{base: '0 15px', lg: '0 100px'}}>
                    <WishlistInCart />
                </Box>
            )}

            <EmptySearchResults openQuickAddModal={true} />

            {isRegistered && !wishlistItems.length && countryISO === 'US' && (
                <Box
                    padding={{base: '0 15px', lg: '0 100px'}}
                    mb="80px"
                    textAlign="center"
                    sx={{
                        '.wishlist-heading': {
                            justifyContent: 'center'
                        }
                    }}
                >
                    <WishlistInCart />
                </Box>
            )}
        </Box>
    )
}

EmptyCart.propTypes = {
    isRegistered: PropTypes.bool
}

export default EmptyCart
